/**
 * NYLSU ERP — translations
 * Two-language (en, tr) flat dictionary.
 * Key convention: <scope>.<name> — e.g. "nav.commandCenter", "collection.status.Concept"
 * If a key is missing in the current language, the raw key or provided default is returned.
 */

const en = {
  // Common
  "common.appName": "NYLSU",
  "common.appTagline": "Operations · Paris / Istanbul",
  "common.timezone": "Timezone",
  "common.actions": "Actions",
  "common.status": "Status",
  "common.description": "Description",
  "common.notes": "Notes",
  "common.name": "Name",
  "common.code": "Code",
  "common.type": "Type",
  "common.country": "Country",
  "common.city": "City",
  "common.email": "Email",
  "common.phone": "Phone",
  "common.currency": "Currency",
  "common.category": "Category",
  "common.unit": "Unit",
  "common.color": "Color",
  "common.size": "Size",
  "common.supplier": "Supplier",
  "common.location": "Location",
  "common.stock": "Stock",
  "common.reorder": "Reorder",
  "common.unitCost": "Unit cost",
  "common.leadTime": "Lead time",
  "common.days": "days",
  "common.reference": "Reference",
  "common.delta": "Delta",
  "common.timestamp": "Timestamp",
  "common.user": "User",
  "common.action": "Action",
  "common.entity": "Entity",
  "common.entityId": "Entity ID",
  "common.active": "Active",
  "common.inactive": "Inactive",
  "common.cancel": "Cancel",
  "common.save": "Save changes",
  "common.create": "Create",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.download": "Download",
  "common.retail": "Retail",
  "common.wholesale": "Wholesale",
  "common.cost": "Cost",
  "common.variants": "Variants",
  "common.role": "Role",
  "common.password": "Password",
  "common.language": "Language",
  "common.viewAll": "View all",
  "common.searchPlaceholder": "Search SKU, style, material, order…",
  "common.deletedToast": "Deleted",
  "common.deleteConfirm": "Delete this record? This cannot be undone.",
  "common.close": "Close",

  // Auth
  "auth.signIn": "Sign in",
  "auth.subtitle": "The operational brain of the house — inventory, production, and orders in one place.",
  "auth.enter": "Enter",
  "auth.signingIn": "Signing in…",
  "auth.restricted": "Restricted access. All actions are audited.",
  "auth.heroTagline": "Le geste précis d'une maison qui sait exactement ce qu'elle produit.",
  "auth.heroEyebrow": "Collection · FW26",
  "auth.logout": "Log out",
  "auth.loggingOut": "Logging out…",

  // Nav
  "nav.commandCenter": "Command Center",
  "nav.orders": "Orders",
  "nav.products": "Products",
  "nav.collections": "Collections",
  "nav.bom": "Bill of Materials",
  "nav.barcodes": "Barcodes & Labels",
  "barcode.eyebrow": "Products · Barcodes",
  "barcode.title": "Barcodes & Labels",
  "barcode.subtitle": "Assign, generate, print barcode labels. SKU stays the internal master identifier — barcodes are an additional scannable layer.",
  "barcode.searchPlaceholder": "Search product, SKU or barcode…",
  "barcode.bulkPrint": "Print labels",
  "barcode.print": "Print",
  "barcode.printPreview": "Label preview",
  "barcode.labels": "labels",
  "barcode.templateA4": "A4 sheet (3 × 8 grid)",
  "barcode.templateSticker": "40 × 25 mm stickers",
  "barcode.noLabels": "No labels selected",
  "barcode.generate": "Generate",
  "barcode.generated": "Barcode generated",
  "barcode.assign": "Assign",
  "barcode.assigned": "Barcode assigned",
  "barcode.assignTitle": "Assign barcode",
  "barcode.type": "Barcode type",
  "barcode.value": "Barcode value",
  "barcode.reason": "Reason (optional)",
  "barcode.reasonPlaceholder": "e.g. external GS1 from supplier",
  "barcode.conflictTitle": "Already assigned to another SKU",
  "barcode.overwriteConfirm": "This SKU already has barcode {current}. Replace with a new internal barcode?",
  "barcode.deactivate": "Deactivate",
  "barcode.deactivated": "Barcode deactivated",
  "barcode.deactivateConfirm": "Deactivate this barcode? It will no longer resolve on scan.",
  "barcode.default": "Default (SKU)",
  "barcode.inactive": "Inactive",
  "barcode.col.product": "Product",
  "barcode.col.color": "Color",
  "barcode.col.size": "Size",
  "barcode.col.sku": "SKU",
  "barcode.col.barcode": "Barcode",
  "barcode.col.type": "Type",
  "barcode.col.status": "Status",
  "barcode.col.price": "Price",
  "barcode.col.labelsQty": "Qty",
  "qr.rollTitle": "Fabric roll QR label",
  "qr.fabric": "Fabric",
  "common.print": "Print",
  "common.refresh": "Refresh",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.empty": "Nothing to show",
  "rolls.qr": "QR",
  "nav.production": "Production",
  "nav.materials": "Materials",
  "nav.fabricRolls": "Fabric Rolls",
  "nav.purchasing": "Purchasing",
  "nav.inventory": "Inventory",
  "nav.customers": "Customers",
  "nav.reports": "Reports",
  "nav.integrations": "Integrations",
  "nav.admin": "Admin",
  "nav.soon": "Soon",

  // Command Center
  "cc.eyebrow": "Command Center",
  "cc.greeting": "Bonjour, {name}",
  "cc.subtitle": "A calm read on the house. Inventory, materials, collections and the health of every location — at a glance.",
  "cc.kpi.styles": "Styles",
  "cc.kpi.materials": "Materials",
  "cc.kpi.fabricOnHand": "Fabric on hand",
  "cc.kpi.inventoryValue": "Inventory value",
  "cc.kpi.lowStock": "Low stock",
  "cc.kpi.locations": "Locations",
  "cc.kpi.suppliers": "Suppliers",
  "cc.kpi.fabricRolls": "Fabric rolls",
  "cc.kpi.reservedSuffix": "{reserved} m reserved · {rolls} rolls",
  "cc.kpi.stylesSub": "{n} collections",
  "cc.kpi.materialsSub": "{n} suppliers",
  "cc.kpi.inventoryValueSub": "Raw materials at cost",
  "cc.kpi.lowStockSub": "Below reorder point",
  "cc.kpi.locationsSub": "Ateliers · Showrooms · Warehouses",
  "cc.kpi.suppliersSub": "Fabric · Accessories · Packaging",
  "cc.kpi.rollsSub": "Per-roll traceability",
  "cc.lowStock.eyebrow": "Requires attention",
  "cc.lowStock.title": "Critical material shortages",
  "cc.lowStock.empty": "Everything is stocked above reorder points. The house breathes.",
  "cc.lowStock.shortage": "Shortage",
  "cc.collections.eyebrow": "By season",
  "cc.collections.title": "Styles per collection",
  "cc.locations.eyebrow": "Distribution",
  "cc.locations.title": "Inventory by location",
  "cc.locations.unitsOnHand": "Units on hand",
  "cc.locations.movements": "Movements",

  // Collections
  "collections.title": "Collections",
  "collections.eyebrow": "Products",
  "collections.subtitle": "Every style belongs to a season.",
  "collections.new": "New collection",
  "collections.edit": "Edit collection",
  "collections.season": "Season",
  "collections.year": "Year",
  "collections.empty": "No collections yet.",
  "collections.createdToast": "Collection created",
  "collections.updatedToast": "Collection updated",
  "collections.createCta": "Create collection",
  "collection.status.Concept": "Concept",
  "collection.status.Development": "Development",
  "collection.status.Sampling": "Sampling",
  "collection.status.Production": "Production",
  "collection.status.Launched": "Launched",
  "collection.status.Archived": "Archived",
  "season.FW": "FW",
  "season.SS": "SS",
  "season.Resort": "Resort",
  "season.Pre-Fall": "Pre-Fall",

  // Styles
  "styles.title": "Styles & SKUs",
  "styles.eyebrow": "Products",
  "styles.subtitle": "Parent styles with color × size variants.",
  "styles.new": "New style",
  "styles.edit": "Edit {code}",
  "styles.styleCode": "Style code",
  "styles.empty": "No styles yet. Click 'New style' to begin.",
  "styles.createdToast": "Style created",
  "styles.updatedToast": "Style updated",
  "styles.createCta": "Create style",
  "styles.deleteConfirm": "Delete style {code}?",
  "styles.variants.title": "Variants (color × size)",
  "styles.variants.hint": "SKU codes will auto-generate as {STYLE}-{COLOR}-{SIZE}",
  "styles.variants.add": "+ Add variant",
  "styles.photosHeading": "Photos & tech drawings",
  "style.category.Dress": "Dress",
  "style.category.Evening Dress": "Evening Dress",
  "style.category.Gown": "Gown",
  "style.category.Top": "Top",
  "style.category.Blouse": "Blouse",
  "style.category.Skirt": "Skirt",
  "style.category.Pants": "Pants",
  "style.category.Jacket": "Jacket",
  "style.category.Coat": "Coat",
  "style.category.Suit": "Suit",
  "style.category.Accessories": "Accessories",
  "style.category.Other": "Other",
  "style.status.Concept": "Concept",
  "style.status.Sample": "Sample",
  "style.status.Approved Sample": "Approved Sample",
  "style.status.Production Ready": "Production Ready",
  "style.status.In Production": "In Production",
  "style.status.Active": "Active",
  "style.status.Discontinued": "Discontinued",
  "style.status.Archived": "Archived",

  // Materials
  "materials.title": "Raw materials",
  "materials.eyebrow": "Materials",
  "materials.subtitle": "Fabrics, accessories, packaging — the building blocks.",
  "materials.new": "New material",
  "materials.empty": "No materials yet. Click 'New material' to begin.",
  "materials.createdToast": "Material created",
  "materials.updatedToast": "Material updated",
  "materials.createCta": "Create material",
  "materials.deleteConfirm": "Delete material {code}?",
  "materials.form.composition": "Composition",
  "materials.form.minStock": "Min. stock",
  "materials.form.reorderPt": "Reorder pt",
  "materials.form.leadTimeDays": "Lead time (days)",
  "materials.form.unitCostEur": "Unit cost (€)",
  "materials.photosHeading": "Fabric photos & spec sheets",
  "material.category.Main Fabric": "Main Fabric",
  "material.category.Lining": "Lining",
  "material.category.Interlining": "Interlining",
  "material.category.Lace": "Lace",
  "material.category.Tulle": "Tulle",
  "material.category.Embroidery": "Embroidery",
  "material.category.Beads": "Beads",
  "material.category.Crystals": "Crystals",
  "material.category.Buttons": "Buttons",
  "material.category.Zippers": "Zippers",
  "material.category.Hooks": "Hooks",
  "material.category.Thread": "Thread",
  "material.category.Labels": "Labels",
  "material.category.Care Labels": "Care Labels",
  "material.category.Packaging": "Packaging",
  "material.category.Garment Bags": "Garment Bags",
  "material.category.Boxes": "Boxes",
  "material.category.Other": "Other",
  "material.unit.meter": "meter",
  "material.unit.centimeter": "centimeter",
  "material.unit.piece": "piece",
  "material.unit.kilogram": "kilogram",
  "material.unit.gram": "gram",
  "material.unit.roll": "roll",
  "material.unit.pack": "pack",
  "material.unit.set": "set",

  // Fabric rolls
  "rolls.title": "Fabric rolls",
  "rolls.eyebrow": "Materials",
  "rolls.subtitle": "Each roll is a story — meters, dye lot, location.",
  "rolls.new": "New roll",
  "rolls.rollNumber": "Roll #",
  "rolls.fabric": "Fabric",
  "rolls.dyeLot": "Dye lot",
  "rolls.received": "Received",
  "rolls.current": "Current",
  "rolls.reserved": "Reserved",
  "rolls.empty": "No fabric rolls yet.",
  "rolls.createdToast": "Roll created",
  "rolls.updatedToast": "Roll updated",
  "rolls.createCta": "Add roll",
  "rolls.deleteConfirm": "Delete roll {number}?",

  // Suppliers
  "suppliers.title": "Suppliers",
  "suppliers.eyebrow": "Purchasing",
  "suppliers.subtitle": "Every mill, atelier and partner behind the collection.",
  "suppliers.new": "New supplier",
  "suppliers.empty": "No suppliers yet.",
  "suppliers.createdToast": "Supplier created",
  "suppliers.updatedToast": "Supplier updated",
  "suppliers.createCta": "Create supplier",
  "suppliers.deleteConfirm": "Delete supplier {name}?",
  "suppliers.contact": "Contact person",
  "suppliers.photosHeading": "Contracts, invoices & swatches",
  "supplier.type.Fabric": "Fabric",
  "supplier.type.Accessories": "Accessories",
  "supplier.type.Packaging": "Packaging",
  "supplier.type.Atelier": "Atelier",
  "supplier.type.Embroidery": "Embroidery",
  "supplier.type.Logistics": "Logistics",
  "supplier.type.Other": "Other",

  // Inventory
  "inventory.title": "Stock ledger",
  "inventory.eyebrow": "Inventory",
  "inventory.subtitle": "Every unit here is the sum of its movements. Nothing overwritten.",
  "inventory.adjustBtn": "Adjust stock",
  "inventory.tab.finished": "Finished goods",
  "inventory.tab.materials": "Raw materials",
  "inventory.tab.movements": "Recent movements",
  "inventory.skuCode": "SKU / Code",
  "inventory.onHand": "On hand",
  "inventory.emptyMovements": "No movements yet.",
  "inventory.emptyStock": "No stock yet.",
  "inventory.adjust.title": "Stock adjustment",
  "inventory.adjust.sku": "SKU / material code",
  "inventory.adjust.quantity": "Quantity (± signed)",
  "inventory.adjust.reason": "Reason",
  "inventory.adjust.postedToast": "Adjustment posted",
  "inventory.adjust.submit": "Post adjustment",
  "inventory.reason.Stock Count": "Stock Count",
  "inventory.reason.Damage": "Damage",
  "inventory.reason.Loss": "Loss",
  "inventory.reason.Correction": "Correction",
  "inventory.reason.Production Waste": "Production Waste",
  "inventory.reason.Other": "Other",
  "inventory.movementType.purchase_receipt": "Purchase Receipt",
  "inventory.movementType.production_output": "Production Output",
  "inventory.movementType.adjustment": "Adjustment",
  "inventory.movementType.sale": "Sale",
  "inventory.movementType.return": "Return",
  "inventory.movementType.transfer_out": "Transfer Out",
  "inventory.movementType.transfer_in": "Transfer In",
  "inventory.movementType.production_consumption": "Production Consumption",
  "inventory.movementType.reservation": "Reservation",
  "inventory.movementType.reservation_release": "Reservation Release",

  // BOM
  "bom.title": "Bill of Materials",
  "bom.eyebrow": "Products",
  "bom.subtitle": "What each style is made of — down to the button.",
  "bom.new": "New BOM",
  "bom.edit": "Edit BOM",
  "bom.empty": "No BOMs yet. Create one to link a style to its materials.",
  "bom.style": "Style",
  "bom.colorHint": "Color (optional — leave blank for base BOM)",
  "bom.title.header": "Bill of materials",
  "bom.addMaterial": "+ Add material",
  "bom.material": "Material",
  "bom.qtyPerUnit": "Qty / unit",
  "bom.waste": "Waste %",
  "bom.qty": "Qty",
  "bom.wastePlaceholder": "Waste %",
  "bom.deleteConfirm": "Delete this BOM?",
  "bom.createCta": "Create BOM",
  "bom.saveCta": "Save BOM",
  "bom.createdToast": "BOM created",
  "bom.updatedToast": "BOM updated",
  "bom.notFound": "Style not found",

  // Integrations
  "integrations.title": "External systems",
  "integrations.eyebrow": "Integrations",
  "integrations.subtitle": "Adapters ready. Add credentials to go live.",
  "integrations.demoBadge": "Demo · not connected",
  "integrations.configureCta": "Configure credentials →",

  // Admin
  "admin.title": "Access & audit",
  "admin.eyebrow": "Admin",
  "admin.subtitle": "Users, roles, locations and every important action recorded.",
  "admin.tab.users": "Users",
  "admin.tab.roles": "Roles",
  "admin.tab.locations": "Locations",
  "admin.tab.audit": "Audit log",
  "admin.newUser": "New user",
  "admin.inviteUser": "Invite user",
  "admin.editUser": "Edit {email}",
  "admin.newLocation": "New location",
  "admin.editLocation": "Edit {name}",
  "admin.newPassword": "New password (optional)",
  "admin.passwordHint": "Leave blank to keep",
  "admin.userCreatedToast": "User created",
  "admin.userUpdatedToast": "User updated",
  "admin.userDeleteConfirm": "Delete user {email}?",
  "admin.locationCreatedToast": "Location created",
  "admin.locationUpdatedToast": "Location updated",
  "admin.locationDeleteConfirm": "Delete location {name}?",
  "admin.locationCreateCta": "Create location",
  "admin.userCreateCta": "Create user",
  "admin.cannotDeleteSelf": "You cannot delete yourself.",
  "admin.auditEmpty": "No audit entries yet.",

  // Location types
  "location.type.atelier": "atelier",
  "location.type.showroom": "showroom",
  "location.type.warehouse": "warehouse",
  "location.type.virtual": "virtual",
  "location.type.store": "store",
  "location.type.consignment": "consignment",

  // Role labels (server also sends role_label but we localize here for consistency)
  "role.admin": "Administrator",
  "role.management": "Management",
  "role.production_manager": "Production Manager",
  "role.atelier_staff": "Atelier Staff",
  "role.inventory": "Inventory / Warehouse",
  "role.showroom_staff": "Showroom Staff",
  "role.purchasing": "Purchasing",
  "role.ecommerce": "E-commerce",
  "role.finance": "Finance",

  // Attachment gallery
  "attach.savePrompt": "Save this record first to attach photos & documents.",
  "attach.drop": "Drop photos or PDFs here, or click to browse",
  "attach.uploading": "Uploading…",
  "attach.constraints": "JPG · PNG · WEBP · PDF · up to {mb} MB",
  "attach.empty": "No attachments yet.",
  "attach.deleteConfirm": "Delete {name}?",
  "attach.uploadedToast": "{name} uploaded",
  "attach.primary": "Primary",
  "attach.setPrimary": "Set as primary",
  "attach.setPrimaryToast": "Set as primary image",
  "attach.unsetPrimaryToast": "Primary image cleared",
  "attach.noKind": "— Kind —",
  "attach.kind.Front": "Front",
  "attach.kind.Back": "Back",
  "attach.kind.Side": "Side",
  "attach.kind.Detail": "Detail",
  "attach.kind.Campaign": "Campaign",
  "attach.kind.Lookbook": "Lookbook",
  "attach.kind.Sample": "Sample",
  "attach.kind.Technical": "Technical drawing",
  "attach.kind.Invoice": "Invoice",
  "attach.kind.Contract": "Contract",
  "attach.kind.Other": "Other",
  "view.list": "List",
  "view.grid": "Grid",
  "styles.thumb": "Photo",
  "styles.noImage": "No image",

  // Coming soon
  "coming.eyebrow": "Phase 2 · 3",
  "coming.title": "Arriving in the next phase",
  "coming.subtitle": "The data model is in place. This section will come to life in the next build.",
  "coming.orders.title": "Orders",
  "coming.orders.sub": "Unified orders across Shopify, showroom, wholesale and made-to-order.",
  "coming.production.title": "Production",
  "coming.production.sub": "Kanban board, MRP, work orders and QC — coming in Phase 2.",
  "coming.purchasing.title": "Purchasing",
  "coming.purchasing.sub": "Purchase requests, POs, goods receipts and supplier scoring.",
  "coming.customers.title": "Customers",
  "coming.customers.sub": "Lightweight CRM and VIP client care.",
  "coming.reports.title": "Reports",
  "coming.reports.sub": "Exportable reports across inventory, production and sales.",

  "common.image": "Image",
  "common.listView": "List view",
  "common.gridView": "Grid view",

  "materials.form.colorName": "Color name",
  "materials.form.colorHex": "Color HEX",
  "materials.form.colorPreview": "Swatch preview",
  "rolls.photosHeading": "Roll photographs",

  "nav.fabricLibrary": "Fabric library",

  "library.eyebrow": "Materials",
  "library.title": "Fabric library",
  "library.subtitle": "A visual catalog of every fabric available to NYLSU — filter by category, composition, supplier or availability.",
  "library.allCategories": "All categories",
  "library.allCompositions": "All compositions",
  "library.allSuppliers": "All suppliers",
  "library.anyAvailability": "Any availability",
  "library.inStock": "In stock",
  "library.outOfStock": "Out of stock",
  "library.composition": "Composition",
  "library.availability": "Availability",
  "library.rolls": "rolls",
  "library.empty": "No fabrics match the selected filters.",

  // ─── Phase 2: Orders / Production / MTO / QC ───
  "common.loading": "Loading…",

  "nav.ordersAll": "All orders",
  "nav.ordersShowroom": "Showroom orders",
  "nav.ordersMTO": "Made-to-order",
  "nav.reservations": "Reservations",
  "nav.productionPlanning": "Production planning",
  "nav.productionOrders": "Production orders",
  "nav.wipBoard": "WIP board",
  "nav.materialRequirements": "Material requirements",
  "nav.qc": "Quality control",
  "nav.productionDashboard": "Production dashboard",
  "nav.ordersDashboard": "Orders dashboard",

  // Customers
  "customers.eyebrow": "Customers",
  "customers.title": "Customers",
  "customers.subtitle": "Retail, wholesale, showroom and made-to-order clients.",
  "customers.new": "New customer",
  "customers.edit": "Edit customer",
  "customers.empty": "No customers yet.",
  "customers.createCta": "Create customer",
  "customers.createdToast": "Customer created",
  "customers.updatedToast": "Customer updated",
  "customers.deleteConfirm": "Delete {name}?",
  "customers.company": "Company",
  "customers.vip": "VIP client",
  "customer.type.retail": "Retail",
  "customer.type.showroom": "Showroom",
  "customer.type.wholesale": "Wholesale",
  "customer.type.mto": "Made-to-Order",
  "customer.type.vip": "VIP",
  "customer.type.other": "Other",

  // Orders
  "orders.eyebrow": "Orders",
  "orders.title": "All orders",
  "orders.showroomTitle": "Showroom orders",
  "orders.subtitle": "Every customer order — from booking to delivery.",
  "orders.new": "New order",
  "orders.empty": "No orders yet.",
  "orders.createOrder": "Create order",
  "orders.createMto": "Create made-to-order",
  "orders.createdToast": "Order {number} created",
  "orders.confirmedToast": "Order confirmed. Availability computed.",
  "orders.reservedToast": "Stock reserved",
  "orders.reqCreatedToast": "Production requirement created",
  "orders.cancelledToast": "Order cancelled — reservations released",
  "orders.cancelConfirm": "Cancel this order and release all reservations?",
  "orders.orderNumber": "Order #",
  "orders.customer": "Customer",
  "orders.source": "Source",
  "orders.priority": "Priority",
  "orders.date": "Order date",
  "orders.deliveryDate": "Delivery",
  "orders.eventDate": "Event date",
  "orders.delivery": "Delivery",
  "orders.total": "Total",
  "orders.items": "Items",
  "orders.qty": "Qty",
  "orders.reserved": "Reserved",
  "orders.unitPrice": "Unit price",
  "orders.confirm": "Confirm order",
  "orders.cancel": "Cancel order",
  "orders.suggested": "Availability & suggested actions",
  "orders.requested": "Requested",
  "orders.availableStock": "Available stock",
  "orders.canReserve": "Can reserve",
  "orders.needsProduction": "Needs production",
  "orders.reserveN": "Reserve {n}",
  "orders.createReqN": "Create production ({n})",
  "orders.selectStyle": "Select style",
  "orders.selectVariant": "Select color / size",
  "orders.addItem": "Add item",
  "orders.internalNotes": "Internal notes",
  "orders.col.style": "Style",
  "orders.col.variant": "Color / Size",
  "orders.col.qty": "Qty",
  "orders.col.unitPrice": "Unit price",
  "orders.col.discount": "Discount %",
  "orders.col.remove": "Remove",
  "orders.needAtLeastOneItem": "At least one item is required",
  "orders.noDate": "No date",
  "orders.dashboardEyebrow": "Overview",
  "orders.dashboardTitle": "Orders dashboard",
  "orders.dashboardSub": "Every order requiring attention today.",
  "orders.kpi.new": "New orders",
  "orders.kpi.productionRequired": "Production required",
  "orders.kpi.inProduction": "In production",
  "orders.kpi.ready": "Ready",
  "orders.kpi.late": "Late",
  "orders.kpi.vipUrgent": "VIP / Urgent",
  "orders.lateList": "Late orders",

  "order.source.manual": "Manual",
  "order.source.showroom": "Showroom",
  "order.source.mto": "Made-to-Order",
  "order.source.wholesale": "Wholesale",
  "order.source.shopify": "Shopify",
  "order.source.paris": "Paris",
  "order.source.other": "Other",

  "order.status.draft": "Draft",
  "order.status.confirmed": "Confirmed",
  "order.status.inventory_check": "Inventory check",
  "order.status.reserved": "Reserved",
  "order.status.production_required": "Production required",
  "order.status.in_production": "In production",
  "order.status.partially_ready": "Partially ready",
  "order.status.ready": "Ready",
  "order.status.packed": "Packed",
  "order.status.delivered": "Delivered",
  "order.status.cancelled": "Cancelled",

  "priority.normal": "Normal",
  "priority.high": "High",
  "priority.urgent": "Urgent",
  "priority.vip": "VIP",

  // Reservations
  "reservations.eyebrow": "Orders",
  "reservations.title": "Reservations",
  "reservations.subtitle": "Stock reserved against confirmed orders.",
  "reservations.empty": "No reservations yet.",
  "reservations.release": "Release",
  "reservation.status.active": "Active",
  "reservation.status.released": "Released",
  "reservation.status.consumed": "Consumed",

  // MTO
  "mto.eyebrow": "Orders",
  "mto.title": "Made-to-order",
  "mto.subtitle": "Custom pieces built to individual measurements.",
  "mto.new": "New made-to-order",
  "mto.measurements": "Measurements",
  "mto.specialInstructions": "Special instructions",
  "mto.m.bust": "Bust",
  "mto.m.waist": "Waist",
  "mto.m.hip": "Hip",
  "mto.m.shoulder": "Shoulder",
  "mto.m.back_width": "Back width",
  "mto.m.dress_length": "Dress length",
  "mto.m.sleeve_length": "Sleeve length",
  "mto.m.height": "Height",

  // Planning
  "planning.eyebrow": "Production",
  "planning.title": "Production planning",
  "planning.subtitle": "Demand vs finished stock vs in-production — the net requirement drives production orders.",
  "planning.empty": "No planning requirements yet.",
  "planning.style": "Style",
  "planning.demand": "Demand",
  "planning.availableFG": "Available FG",
  "planning.inProduction": "In production",
  "planning.net": "Net requirement",
  "planning.createPO": "Create production order",

  // Production Orders
  "po.eyebrow": "Production",
  "po.title": "Production orders",
  "po.subtitle": "Every production order — from BOM explosion to finished goods.",
  "po.new": "New production order",
  "po.empty": "No production orders yet.",
  "po.number": "PO #",
  "po.qty": "Qty",
  "po.stage": "Stage",
  "po.units": "units",
  "po.sizeBreakdown": "Size breakdown",
  "po.needSizeBreakdown": "Enter at least one size quantity",
  "po.total": "Total",
  "po.plannedCompletion": "Planned completion",
  "po.completion": "Completion",
  "po.createCta": "Create production order",
  "po.createdToast": "Production order {number} created",
  "po.materials": "Materials",
  "po.materialAvailability": "Material availability",
  "po.material.green": "All materials available",
  "po.material.amber": "Watch reorder levels",
  "po.material.red": "Shortage — cannot fully supply",
  "po.stdQty": "Standard",
  "po.requiredQty": "Required",
  "po.reservedQty": "Reserved",
  "po.consumedQty": "Consumed",
  "po.availableQty": "Available",
  "po.shortage": "Shortage",
  "po.noMaterials": "No BOM linked — check style BOM setup.",
  "po.reserve": "Reserve",
  "po.consume": "Consume",
  "po.reserveMaterial": "Reserve material",
  "po.consumeMaterial": "Consume material",
  "po.reserveQty": "Quantity to reserve",
  "po.actualQty": "Actual consumption",
  "po.variance": "Variance",
  "po.varianceReason": "Variance reason",
  "po.consumeFromRoll": "From roll",
  "po.rollAllocation": "Fabric roll allocation",
  "po.allocated": "Allocated",
  "po.advanceStage": "Advance stage",
  "po.advance": "Advance",
  "po.currentStage": "Current stage",
  "po.toStage": "To stage",
  "po.stageAdvancedToast": "Stage advanced",
  "po.receiveFinished": "Receive finished goods",
  "po.receiveSku": "SKU",
  "po.availableToReceive": "available",
  "po.receive": "Receive",
  "po.receivedToast": "Finished goods received into inventory",
  "po.reservedToast": "Material reserved",
  "po.consumedToast": "Consumption posted",
  "po.items": "Items",
  "po.received": "Received",
  "po.from": "From",
  "po.to": "To",
  "po.stageHistory": "Stage history",
  "po.cancel": "Cancel PO",
  "po.cancelledToast": "Production order cancelled",
  "po.cancelConfirm": "Cancel this production order? Reserved materials will be released.",

  "po.status.draft": "Draft",
  "po.status.material_check": "Material check",
  "po.status.material_shortage": "Material shortage",
  "po.status.ready_to_start": "Ready to start",
  "po.status.in_production": "In production",
  "po.status.qc": "Quality control",
  "po.status.completed": "Completed",
  "po.status.on_hold": "On hold",
  "po.status.cancelled": "Cancelled",

  // WIP
  "wip.eyebrow": "Production",
  "wip.title": "WIP board",
  "wip.subtitle": "Every piece moving through the atelier — click a card to advance its stage.",
  "wip.openFullDetail": "Open full detail →",

  // Material Requirements
  "matreq.eyebrow": "Production",
  "matreq.title": "Material requirements",
  "matreq.subtitle": "Consolidated material shortages across open production orders.",
  "matreq.empty": "No material requirements — everything is supplied.",
  "matreq.affectedPOs": "Open POs",

  // QC
  "qc.eyebrow": "Production",
  "qc.title": "Quality control",
  "qc.subtitle": "Every inspection recorded — passed pieces enter sellable inventory.",
  "qc.record": "Record QC",
  "qc.recordedToast": "QC record saved",
  "qc.empty": "No QC records yet.",
  "qc.sku": "SKU",
  "qc.inspected": "Inspected",
  "qc.passed": "Passed",
  "qc.failed": "Failed",
  "qc.failureReason": "Failure reason",
  "qc.disposition": "Disposition",
  "qc.inspector": "Inspector",

  // Production dashboard
  "prod.dashboardEyebrow": "Overview",
  "prod.dashboardTitle": "Production dashboard",
  "prod.dashboardSub": "The heartbeat of the atelier.",
  "prod.kpi.openPOs": "Open POs",
  "prod.kpi.unitsInProduction": "Units in production",
  "prod.kpi.completedToday": "Completed today",
  "prod.kpi.late": "Late",
  "prod.kpi.atRisk": "At risk",
  "prod.kpi.materialShortages": "Material shortages",
  "prod.kpi.qcFailures": "QC failures",
  "prod.byStage": "By stage",

  "phase2.late": "Late",
  "phase2.atRisk": "At risk",

  // ── Phase 2.5 — Sales / POS
  "nav.salesDashboard": "Sales dashboard",
  "nav.salesNew": "New sale",
  "nav.salesAll": "All sales",
  "nav.salesShowroom": "Showroom sales",
  "nav.salesReturns": "Returns",

  "common.date": "Date",

  "pos.eyebrow": "Sales",
  "pos.title": "New sale",
  "pos.subtitle": "Physical showroom sale — inventory, COGS and invoice queue happen automatically.",
  "pos.needLocation": "Select a selling location first",
  "pos.searchPlaceholder": "Search style, SKU, color, size…",
  "pos.noResults": "No matching products",
  "pos.available": "available",
  "pos.outOfStock": "Out of stock",
  "pos.currentSale": "Current sale",
  "pos.newSaleDraft": "New sale (draft)",
  "pos.lines": "lines",
  "pos.cartEmpty": "Cart is empty — add a product from the left.",
  "pos.searchScanPlaceholder": "Search product, SKU or scan barcode…",
  "pos.scan": "Scan",
  "pos.scanAdded": "{name} · {sku} added",
  "pos.scanNotFound": "Barcode not found: {code}",
  "pos.scanInactive": "Barcode is inactive",
  "pos.scanOutOfStock": "{name} — out of stock at this location",
  "orders.priceFromMaster": "Price is set from the Product Master. Ask a manager to override.",
  "styles.searchPlaceholder": "Search by product name, style code, SKU or barcode…",
  "suppliers.searchPlaceholder": "Search suppliers by name, contact, email, city…",
  "customers.searchPlaceholder": "Search customers by name, phone or email…",
  "inventory.searchPlaceholder": "Search by product, SKU, color, size or material code…",
  "bom.searchPlaceholder": "Search product recipes by name, code or SKU…",
  "bom.subtitleList": "Every product is one row. Click to open its full recipe.",
  "bom.status": "Recipe status",
  "bom.status.no_bom": "No recipe",
  "bom.status.draft": "Draft",
  "bom.status.ready": "Ready",
  "bom.open": "Open recipe",
  "bom.backToList": "Back to Product recipes",
  "bom.editRecipe": "Edit recipe",
  "bom.createRecipe": "+ Create recipe",
  "bom.noRecipe": "This product has no recipe yet.",
  "common.all": "All",
  "common.clearFilters": "Clear filters",
  "common.noMatches": "No matches for the current filters.",
  "common.delete": "Delete",
  "pos.walkIn": "Walk-in customer",
  "stock.addStock": "Add stock",
  "stock.openingStockHeading": "Opening stock (optional)",
  "stock.openingStockHint": "If you already hold physical stock of this material, enter the quantity so we create an OPENING STOCK movement in the ledger.",
  "stock.physical": "Physical",
  "stock.reserved": "Reserved",
  "stock.available": "Available",
  "stock.direction": "Direction",
  "stock.increase": "Increase (+)",
  "stock.decrease": "Decrease (−)",
  "stock.afterAdjustment": "After adjustment:",
  "stock.belowReserved": "Adjustment cannot reduce physical stock below reserved quantity.",
  "stock.needLocation": "Location is required.",
  "stock.needQty": "Quantity must be greater than 0.",
  "stock.saved": "Stock movement recorded.",
  "common.reason": "Reason",
  "common.notes": "Notes",
  "costing.panelTitle": "Cost & pricing",
  "costing.noBom": "No active recipe. Create one to see cost.",
  "costing.materials": "Raw materials",
  "costing.labor": "Labor",
  "costing.overhead": "Overhead",
  "costing.totalCost": "Total product cost",
  "costing.materialsPct": "Materials share",
  "costing.laborPct": "Labor share",
  "costing.overheadPct": "Overhead share",
  "costing.method": "Pricing method",
  "costing.markup": "Markup",
  "costing.margin": "Target margin",
  "costing.suggestedPrice": "Suggested selling price",
  "costing.currentRetail": "Current retail price",
  "costing.gpAtRetail": "GP at current retail",
  "costing.gmAtRetail": "GM at current retail",
  "costing.applyPrice": "Apply suggested price",
  "costing.applyConfirm": "Replace current retail with the suggested price?",
  "costing.applied": "Retail price updated from BOM cost.",
  "costing.incompleteWarn": "Cost incomplete — missing material cost: {missing}",
  "costing.sectionMaterials": "Raw materials",
  "costing.sectionLabor": "Labor",
  "costing.sectionOverhead": "Overhead",
  "costing.laborType": "Labor type",
  "costing.atelier": "Atelier / master",
  "costing.operation": "Operation",
  "costing.cost": "Cost",
  "costing.unitCost": "Unit cost",
  "costing.lineCost": "Line cost",
  "costing.noLabor": "No labor lines yet.",
  "costing.fixed": "Fixed per garment",
  "costing.percent": "% of material + labor",
  "costing.missing": "Missing cost",
  "common.quantity": "Qty",
  "common.none": "None",
  "nav.finance": "Finance",
  "nav.financeDashboard": "Finance dashboard",
  "nav.expenses": "Expenses",
  "nav.payables": "Supplier payables",
  "finance.dashboard": "Finance dashboard",
  "finance.subtitle": "Live view of NYLSU cash position, expenses and supplier payables.",
  "finance.cash": "Cash",
  "finance.bank": "Bank",
  "finance.card": "Card / POS",
  "finance.other": "Other",
  "finance.collectionsMTD": "Collections · MTD",
  "finance.expensesMTD": "Expenses · MTD",
  "finance.purchaseMTD": "Purchase payments · MTD",
  "finance.receivables": "Outstanding receivables",
  "finance.payables": "Outstanding payables",
  "finance.payablesSubtitle": "What NYLSU owes suppliers, aggregated from Purchase Orders.",
  "finance.accounts": "Financial accounts",
  "finance.opening": "Opening",
  "finance.moneyIn": "In",
  "finance.moneyOut": "Out",
  "finance.balance": "Balance",
  "finance.poNumber": "PO #",
  "finance.poTotal": "PO total",
  "finance.paid": "Paid",
  "finance.remaining": "Remaining",
  "finance.noPayables": "No open supplier payables.",
  "expense.title": "Expenses",
  "expense.subtitle": "Structured operational expense archive with categories, campaigns and attachments.",
  "expense.new": "New expense",
  "expense.number": "Expense #",
  "expense.payee": "Payee",
  "expense.campaign": "Campaign / cost centre",
  "expense.subcategory": "Subcategory",
  "expense.invoiceNumber": "Invoice number",
  "expense.method": "Payment method",
  "expense.account": "Paid from account",
  "expense.searchPlaceholder": "Search by number, description, payee, invoice or campaign…",
  "expense.needDescAmount": "Description and non-zero amount are required.",
  "expense.createdToast": "Expense {number} created",
  "common.date": "Date",
  "common.amount": "Amount",
  "common.description": "Description",
  "common.supplier": "Supplier",
  "pos.customerSearch": "Search customer by name or phone",
  "pos.customerSearchHint": "Type at least 2 characters, or 3+ digits for phone",
  "pos.customerNoResults": "No customer found",
  "pos.customerCreate": "+ Create new customer",
  "pos.customerSelected": "Customer",
  "pos.customerChange": "Change",
  "pos.customerClear": "Use walk-in",
  "pos.newCustomer": "New customer",
  "pos.customerName": "Full name",
  "pos.customerPhone": "Phone",
  "pos.customerEmail": "Email",
  "pos.customerCity": "City",
  "pos.customerType": "Type",
  "pos.customerSave": "Save & select",
  "pos.customerCancel": "Cancel",
  "pos.customerDuplicate": "A customer with this phone number already exists.",
  "pos.customerDuplicateSelect": "Select existing",
  "pos.customerDuplicateContinue": "Create anyway",
  "pos.customerCreated": "Customer created",
  "pos.summary": "Summary",
  "pos.subtotal": "Subtotal",
  "pos.itemDiscount": "Item discounts",
  "pos.saleDiscount": "Sale discount",
  "pos.netSales": "Net sales",
  "pos.tax": "Tax",
  "pos.total": "Total",
  "pos.amountPaid": "Paid",
  "pos.balanceDue": "Balance due",
  "pos.discount": "Discount",
  "pos.reason": "Reason",
  "pos.apply": "Apply",
  "pos.discountNote": "Discounts above 10% require manager approval.",
  "pos.payment": "Payment",
  "pos.recordPayment": "Record",
  "pos.recordSplit": "Record payment",
  "pos.splitRecorded": "{n} payment(s) recorded",
  "pos.preset.cardAll": "Card · all",
  "pos.preset.cashAll": "Cash · all",
  "pos.preset.halfHalf": "50 / 50",
  "sales.viewReceipt": "Receipt",
  "common.back": "Back",
  "pos.completeSale": "Complete sale",
  "pos.completePartial": "Complete with balance due",
  "pos.discountApplied": "Discount applied",
  "pos.paymentAmountRequired": "Enter payment amount",
  "pos.paymentRecorded": "Payment recorded",
  "pos.saleCompleted": "Sale {number} completed",

  "sales.eyebrow": "Sales",
  "sales.title": "All sales",
  "sales.showroomTitle": "Showroom sales",
  "sales.subtitle": "Every physical, MTO and channel sale — with COGS and invoice status.",
  "sales.new": "New sale",
  "sales.empty": "No sales yet.",
  "sales.number": "Sale #",
  "sales.channel": "Channel",
  "sales.units": "Units",
  "sales.grossProfit": "Gross profit",
  "sales.margin": "Margin",
  "sales.invoice": "Invoice",
  "sales.discount": "Disc",
  "sales.unitCost": "Unit cost",
  "sales.integrations": "External integrations",
  "sales.invoiceId": "Invoice ID",
  "sales.retry": "Retry",
  "sales.retryQueued": "Retry queued",
  "sales.print": "Print summary",
  "sales.void": "Void",
  "sales.voidConfirm": "Void this sale? Inventory will be reversed if the sale was completed.",
  "sales.voided": "Sale voided",
  "sales.fullyPaid": "Fully paid",
  "sales.dashEyebrow": "Overview",
  "sales.dashTitle": "Sales dashboard",
  "sales.dashSub": "Today, this month, and the styles moving in the showroom.",
  "sales.topStyles": "Top styles",
  "sales.kpi.todayNet": "Today · net sales",
  "sales.kpi.todayUnits": "Today · units",
  "sales.kpi.mtdNet": "Month · net sales",
  "sales.kpi.mtdMargin": "Month · gross margin",
  "sales.kpi.mtdProfit": "Month · gross profit",
  "sales.kpi.avgSale": "Avg sale today",
  "sales.kpi.returns": "Returns",
  "sales.kpi.failedInvoices": "Failed invoice syncs",

  "sale.channel.showroom": "Showroom",
  "sale.channel.manual": "Manual",
  "sale.channel.shopify": "Shopify",
  "sale.channel.mto": "Made-to-Order",
  "sale.channel.wholesale": "Wholesale",
  "sale.channel.paris": "Paris",
  "sale.channel.other": "Other",
  "sale.status.draft": "Draft",
  "sale.status.completed": "Completed",
  "sale.status.voided": "Voided",

  "payment.method.credit_card": "Credit card",
  "payment.method.cash": "Cash",
  "payment.method.bank_transfer": "Bank transfer",
  "payment.method.payment_link": "Payment link",
  "payment.method.other": "Other",
  "payment.status.unpaid": "Unpaid",
  "payment.status.partial": "Partial",
  "payment.status.paid": "Paid",
  "payment.status.refunded": "Refunded",
  "payment.status.partially_refunded": "Partially refunded",

  "invoice.status.pending": "Pending",
  "invoice.status.processing": "Processing",
  "invoice.status.synced": "Synced",
  "invoice.status.failed": "Failed",
  "invoice.status.retrying": "Retrying",
  "invoice.status.not_required": "Not required",

  "returns.eyebrow": "Sales",
  "returns.title": "Returns",
  "returns.subtitle": "Every return — inspected, dispositioned and reflected in inventory.",
  "returns.new": "New return",
  "returns.empty": "No returns yet.",
  "returns.number": "Return #",
  "returns.reason": "Reason",
  "returns.refund": "Refund",
  "returns.originalSale": "Original sale",
  "returns.saleNotFound": "Sale not found",
  "returns.load": "Load",
  "returns.returnQty": "Return qty",
  "returns.disposition": "Disposition",
  "returns.pickAtLeastOne": "Pick at least one line",
  "returns.createCta": "Create return",
  "returns.createdToast": "Return created",
  "returns.completedToast": "Return inspection completed",
  "returns.completeInspection": "Complete inspection",
};

const tr = {
  "common.appName": "NYLSU",
  "common.appTagline": "Operasyon · Paris / İstanbul",
  "common.timezone": "Saat dilimi",
  "common.actions": "İşlemler",
  "common.status": "Durum",
  "common.description": "Açıklama",
  "common.notes": "Notlar",
  "common.name": "Ad",
  "common.code": "Kod",
  "common.type": "Tür",
  "common.country": "Ülke",
  "common.city": "Şehir",
  "common.email": "E-posta",
  "common.phone": "Telefon",
  "common.currency": "Para birimi",
  "common.category": "Kategori",
  "common.unit": "Birim",
  "common.color": "Renk",
  "common.size": "Beden",
  "common.supplier": "Tedarikçi",
  "common.location": "Konum",
  "common.stock": "Stok",
  "common.reorder": "Sipariş noktası",
  "common.unitCost": "Birim maliyet",
  "common.leadTime": "Teslim süresi",
  "common.days": "gün",
  "common.reference": "Referans",
  "common.delta": "Değişim",
  "common.timestamp": "Zaman damgası",
  "common.user": "Kullanıcı",
  "common.action": "İşlem",
  "common.entity": "Kayıt",
  "common.entityId": "Kayıt No",
  "common.active": "Aktif",
  "common.inactive": "Pasif",
  "common.cancel": "Vazgeç",
  "common.save": "Değişiklikleri kaydet",
  "common.create": "Oluştur",
  "common.edit": "Düzenle",
  "common.delete": "Sil",
  "common.download": "İndir",
  "common.retail": "Perakende",
  "common.wholesale": "Toptan",
  "common.cost": "Maliyet",
  "common.variants": "Varyantlar",
  "common.role": "Rol",
  "common.password": "Parola",
  "common.language": "Dil",
  "common.viewAll": "Tümünü gör",
  "common.searchPlaceholder": "SKU, model, malzeme, sipariş ara…",
  "common.deletedToast": "Silindi",
  "common.deleteConfirm": "Bu kayıt silinsin mi? Bu işlem geri alınamaz.",
  "common.close": "Kapat",

  "auth.signIn": "Giriş yap",
  "auth.subtitle": "Evin operasyonel beyni — stok, üretim ve siparişler tek yerde.",
  "auth.enter": "Giriş",
  "auth.signingIn": "Giriş yapılıyor…",
  "auth.restricted": "Kısıtlı erişim. Tüm işlemler kayıt altına alınır.",
  "auth.heroTagline": "Ürettiği her parçayı bilen bir modaevinin kesin duruşu.",
  "auth.heroEyebrow": "Koleksiyon · SK26",
  "auth.logout": "Çıkış yap",
  "auth.loggingOut": "Çıkılıyor…",

  "nav.commandCenter": "Kumanda Merkezi",
  "nav.orders": "Siparişler",
  "nav.products": "Ürünler",
  "nav.collections": "Koleksiyonlar",
  "nav.bom": "Ürün Reçetesi",
  "nav.barcodes": "Barkod & Etiket",
  "barcode.eyebrow": "Ürünler · Barkodlar",
  "barcode.title": "Barkod & Etiket",
  "barcode.subtitle": "Barkod atama, üretme, etiket basma. SKU dahili ana tanımlayıcı olarak kalır — barkod ek okutulabilir katmandır.",
  "barcode.searchPlaceholder": "Ürün, SKU veya barkod ara…",
  "barcode.bulkPrint": "Etiket bas",
  "barcode.print": "Bas",
  "barcode.printPreview": "Etiket önizleme",
  "barcode.labels": "etiket",
  "barcode.templateA4": "A4 sayfa (3 × 8 grid)",
  "barcode.templateSticker": "40 × 25 mm sticker",
  "barcode.noLabels": "Etiket seçilmedi",
  "barcode.generate": "Üret",
  "barcode.generated": "Barkod üretildi",
  "barcode.assign": "Ata",
  "barcode.assigned": "Barkod atandı",
  "barcode.assignTitle": "Barkod ata",
  "barcode.type": "Barkod tipi",
  "barcode.value": "Barkod",
  "barcode.reason": "Neden (isteğe bağlı)",
  "barcode.reasonPlaceholder": "örn. tedarikçiden GS1",
  "barcode.conflictTitle": "Başka bir SKU'ya atanmış",
  "barcode.overwriteConfirm": "Bu SKU'nun mevcut barkodu {current}. Yeni bir dahili barkod ile değiştirilsin mi?",
  "barcode.deactivate": "Pasifleştir",
  "barcode.deactivated": "Barkod pasifleştirildi",
  "barcode.deactivateConfirm": "Bu barkod pasifleştirilsin mi? Okutulduğunda artık çözümlenmeyecek.",
  "barcode.default": "Varsayılan (SKU)",
  "barcode.inactive": "Pasif",
  "barcode.col.product": "Ürün",
  "barcode.col.color": "Renk",
  "barcode.col.size": "Beden",
  "barcode.col.sku": "SKU",
  "barcode.col.barcode": "Barkod",
  "barcode.col.type": "Tip",
  "barcode.col.status": "Durum",
  "barcode.col.price": "Fiyat",
  "barcode.col.labelsQty": "Adet",
  "qr.rollTitle": "Kumaş rulosu QR etiketi",
  "qr.fabric": "Kumaş",
  "rolls.qr": "QR",
  "nav.production": "Üretim",
  "nav.materials": "Malzemeler",
  "nav.fabricRolls": "Kumaş Topları",
  "nav.purchasing": "Satın Alma",
  "nav.inventory": "Envanter",
  "nav.customers": "Müşteriler",
  "nav.reports": "Raporlar",
  "nav.integrations": "Entegrasyonlar",
  "nav.admin": "Yönetim",
  "nav.soon": "Yakında",

  "cc.eyebrow": "Kumanda Merkezi",
  "cc.greeting": "Merhaba, {name}",
  "cc.subtitle": "Modaevinin sakin bir özeti. Stok, malzemeler, koleksiyonlar ve her konumun sağlığı — bir bakışta.",
  "cc.kpi.styles": "Modeller",
  "cc.kpi.materials": "Malzemeler",
  "cc.kpi.fabricOnHand": "Kumaş stoğu",
  "cc.kpi.inventoryValue": "Envanter değeri",
  "cc.kpi.lowStock": "Düşük stok",
  "cc.kpi.locations": "Konumlar",
  "cc.kpi.suppliers": "Tedarikçiler",
  "cc.kpi.fabricRolls": "Kumaş topları",
  "cc.kpi.reservedSuffix": "{reserved} m rezerve · {rolls} top",
  "cc.kpi.stylesSub": "{n} koleksiyon",
  "cc.kpi.materialsSub": "{n} tedarikçi",
  "cc.kpi.inventoryValueSub": "Ham madde, maliyet üzerinden",
  "cc.kpi.lowStockSub": "Sipariş noktasının altında",
  "cc.kpi.locationsSub": "Atölye · Showroom · Depo",
  "cc.kpi.suppliersSub": "Kumaş · Aksesuar · Ambalaj",
  "cc.kpi.rollsSub": "Top bazlı izlenebilirlik",
  "cc.lowStock.eyebrow": "İlgi bekleyen",
  "cc.lowStock.title": "Kritik malzeme eksikleri",
  "cc.lowStock.empty": "Her şey sipariş noktasının üstünde. Modaevi rahat nefes alıyor.",
  "cc.lowStock.shortage": "Eksik",
  "cc.collections.eyebrow": "Sezona göre",
  "cc.collections.title": "Koleksiyon başına model",
  "cc.locations.eyebrow": "Dağılım",
  "cc.locations.title": "Konum bazlı envanter",
  "cc.locations.unitsOnHand": "Mevcut adet",
  "cc.locations.movements": "Hareket",

  "collections.title": "Koleksiyonlar",
  "collections.eyebrow": "Ürünler",
  "collections.subtitle": "Her model bir sezona aittir.",
  "collections.new": "Yeni koleksiyon",
  "collections.edit": "Koleksiyonu düzenle",
  "collections.season": "Sezon",
  "collections.year": "Yıl",
  "collections.empty": "Henüz koleksiyon yok.",
  "collections.createdToast": "Koleksiyon oluşturuldu",
  "collections.updatedToast": "Koleksiyon güncellendi",
  "collections.createCta": "Koleksiyon oluştur",
  "collection.status.Concept": "Konsept",
  "collection.status.Development": "Geliştirme",
  "collection.status.Sampling": "Numune",
  "collection.status.Production": "Üretim",
  "collection.status.Launched": "Lansman",
  "collection.status.Archived": "Arşiv",
  "season.FW": "SK",
  "season.SS": "İY",
  "season.Resort": "Resort",
  "season.Pre-Fall": "Sonbahar Öncesi",

  "styles.title": "Modeller ve SKU'lar",
  "styles.eyebrow": "Ürünler",
  "styles.subtitle": "Ana modeller ve renk × beden varyantları.",
  "styles.new": "Yeni model",
  "styles.edit": "{code} düzenle",
  "styles.styleCode": "Model kodu",
  "styles.empty": "Henüz model yok. Başlamak için 'Yeni model'e tıklayın.",
  "styles.createdToast": "Model oluşturuldu",
  "styles.updatedToast": "Model güncellendi",
  "styles.createCta": "Model oluştur",
  "styles.deleteConfirm": "{code} modeli silinsin mi?",
  "styles.variants.title": "Varyantlar (renk × beden)",
  "styles.variants.hint": "SKU kodları {STYLE}-{COLOR}-{SIZE} formatında otomatik oluşur",
  "styles.variants.add": "+ Varyant ekle",
  "styles.photosHeading": "Fotoğraflar ve teknik çizimler",
  "style.category.Dress": "Elbise",
  "style.category.Evening Dress": "Abiye",
  "style.category.Gown": "Uzun Elbise",
  "style.category.Top": "Üst",
  "style.category.Blouse": "Bluz",
  "style.category.Skirt": "Etek",
  "style.category.Pants": "Pantolon",
  "style.category.Jacket": "Ceket",
  "style.category.Coat": "Palto",
  "style.category.Suit": "Takım",
  "style.category.Accessories": "Aksesuar",
  "style.category.Other": "Diğer",
  "style.status.Concept": "Konsept",
  "style.status.Sample": "Numune",
  "style.status.Approved Sample": "Onaylı Numune",
  "style.status.Production Ready": "Üretime Hazır",
  "style.status.In Production": "Üretimde",
  "style.status.Active": "Aktif",
  "style.status.Discontinued": "Üretimden Kaldırıldı",
  "style.status.Archived": "Arşiv",

  "materials.title": "Ham malzemeler",
  "materials.eyebrow": "Malzemeler",
  "materials.subtitle": "Kumaşlar, aksesuarlar, ambalaj — yapı taşları.",
  "materials.new": "Yeni malzeme",
  "materials.empty": "Henüz malzeme yok. 'Yeni malzeme'ye tıklayın.",
  "materials.createdToast": "Malzeme oluşturuldu",
  "materials.updatedToast": "Malzeme güncellendi",
  "materials.createCta": "Malzeme oluştur",
  "materials.deleteConfirm": "{code} malzemesi silinsin mi?",
  "materials.form.composition": "Kompozisyon",
  "materials.form.minStock": "Min. stok",
  "materials.form.reorderPt": "Sipariş nok.",
  "materials.form.leadTimeDays": "Teslim süresi (gün)",
  "materials.form.unitCostEur": "Birim maliyet (€)",
  "materials.photosHeading": "Kumaş fotoğrafları ve spesifikasyonlar",
  "material.category.Main Fabric": "Ana Kumaş",
  "material.category.Lining": "Astar",
  "material.category.Interlining": "Tela",
  "material.category.Lace": "Dantel",
  "material.category.Tulle": "Tül",
  "material.category.Embroidery": "Nakış",
  "material.category.Beads": "Boncuk",
  "material.category.Crystals": "Kristal",
  "material.category.Buttons": "Düğme",
  "material.category.Zippers": "Fermuar",
  "material.category.Hooks": "Kopça",
  "material.category.Thread": "İplik",
  "material.category.Labels": "Etiket",
  "material.category.Care Labels": "Bakım Etiketi",
  "material.category.Packaging": "Ambalaj",
  "material.category.Garment Bags": "Kıyafet Torbası",
  "material.category.Boxes": "Kutu",
  "material.category.Other": "Diğer",
  "material.unit.meter": "metre",
  "material.unit.centimeter": "santimetre",
  "material.unit.piece": "adet",
  "material.unit.kilogram": "kilogram",
  "material.unit.gram": "gram",
  "material.unit.roll": "top",
  "material.unit.pack": "paket",
  "material.unit.set": "set",

  "rolls.title": "Kumaş topları",
  "rolls.eyebrow": "Malzemeler",
  "rolls.subtitle": "Her top bir hikaye — metre, boya lotu, konum.",
  "rolls.new": "Yeni top",
  "rolls.rollNumber": "Top #",
  "rolls.fabric": "Kumaş",
  "rolls.dyeLot": "Boya lotu",
  "rolls.received": "Gelen",
  "rolls.current": "Mevcut",
  "rolls.reserved": "Rezerve",
  "rolls.empty": "Henüz kumaş topu yok.",
  "rolls.createdToast": "Top eklendi",
  "rolls.updatedToast": "Top güncellendi",
  "rolls.createCta": "Top ekle",
  "rolls.deleteConfirm": "{number} numaralı top silinsin mi?",

  "suppliers.title": "Tedarikçiler",
  "suppliers.eyebrow": "Satın Alma",
  "suppliers.subtitle": "Her tezgah, atölye ve koleksiyonun arkasındaki ortak.",
  "suppliers.new": "Yeni tedarikçi",
  "suppliers.empty": "Henüz tedarikçi yok.",
  "suppliers.createdToast": "Tedarikçi oluşturuldu",
  "suppliers.updatedToast": "Tedarikçi güncellendi",
  "suppliers.createCta": "Tedarikçi oluştur",
  "suppliers.deleteConfirm": "{name} tedarikçisi silinsin mi?",
  "suppliers.contact": "İletişim kişisi",
  "suppliers.photosHeading": "Sözleşmeler, faturalar ve kumaş örnekleri",
  "supplier.type.Fabric": "Kumaş",
  "supplier.type.Accessories": "Aksesuar",
  "supplier.type.Packaging": "Ambalaj",
  "supplier.type.Atelier": "Atölye",
  "supplier.type.Embroidery": "Nakış",
  "supplier.type.Logistics": "Lojistik",
  "supplier.type.Other": "Diğer",

  "inventory.title": "Stok defteri",
  "inventory.eyebrow": "Envanter",
  "inventory.subtitle": "Her adet, hareketlerinin toplamıdır. Hiçbir kayıt üzerine yazılmaz.",
  "inventory.adjustBtn": "Stok düzenle",
  "inventory.tab.finished": "Bitmiş ürünler",
  "inventory.tab.materials": "Ham madde",
  "inventory.tab.movements": "Son hareketler",
  "inventory.skuCode": "SKU / Kod",
  "inventory.onHand": "Mevcut",
  "inventory.emptyMovements": "Henüz hareket yok.",
  "inventory.emptyStock": "Henüz stok yok.",
  "inventory.adjust.title": "Stok düzeltmesi",
  "inventory.adjust.sku": "SKU / malzeme kodu",
  "inventory.adjust.quantity": "Miktar (± işaretli)",
  "inventory.adjust.reason": "Sebep",
  "inventory.adjust.postedToast": "Düzeltme kaydedildi",
  "inventory.adjust.submit": "Düzeltmeyi kaydet",
  "inventory.reason.Stock Count": "Stok Sayımı",
  "inventory.reason.Damage": "Hasar",
  "inventory.reason.Loss": "Kayıp",
  "inventory.reason.Correction": "Düzeltme",
  "inventory.reason.Production Waste": "Üretim Firesi",
  "inventory.reason.Other": "Diğer",
  "inventory.movementType.purchase_receipt": "Satın Alma Girişi",
  "inventory.movementType.production_output": "Üretim Çıkışı",
  "inventory.movementType.adjustment": "Düzeltme",
  "inventory.movementType.sale": "Satış",
  "inventory.movementType.return": "İade",
  "inventory.movementType.transfer_out": "Transfer (Çıkış)",
  "inventory.movementType.transfer_in": "Transfer (Giriş)",
  "inventory.movementType.production_consumption": "Üretim Tüketimi",
  "inventory.movementType.reservation": "Rezervasyon",
  "inventory.movementType.reservation_release": "Rezervasyon İptali",

  "bom.title": "Ürün Reçetesi",
  "bom.eyebrow": "Ürünler",
  "bom.subtitle": "Her modelin içeriği — düğmesine kadar.",
  "bom.new": "Yeni reçete",
  "bom.edit": "Reçeteyi düzenle",
  "bom.empty": "Henüz reçete yok. Bir modele malzemeleri bağlamak için bir reçete oluşturun.",
  "bom.style": "Model",
  "bom.colorHint": "Renk (opsiyonel — temel reçete için boş bırakın)",
  "bom.title.header": "Ürün reçetesi",
  "bom.addMaterial": "+ Malzeme ekle",
  "bom.material": "Malzeme",
  "bom.qtyPerUnit": "Adet başı miktar",
  "bom.waste": "Fire %",
  "bom.qty": "Miktar",
  "bom.wastePlaceholder": "Fire %",
  "bom.deleteConfirm": "Bu reçete silinsin mi?",
  "bom.createCta": "Reçete oluştur",
  "bom.saveCta": "Reçeteyi kaydet",
  "bom.createdToast": "Reçete oluşturuldu",
  "bom.updatedToast": "Reçete güncellendi",
  "bom.notFound": "Model bulunamadı",

  "integrations.title": "Harici sistemler",
  "integrations.eyebrow": "Entegrasyonlar",
  "integrations.subtitle": "Bağlayıcılar hazır. Canlıya almak için kimlik bilgilerini ekleyin.",
  "integrations.demoBadge": "Demo · bağlı değil",
  "integrations.configureCta": "Kimlik bilgilerini düzenle →",

  "admin.title": "Erişim ve denetim",
  "admin.eyebrow": "Yönetim",
  "admin.subtitle": "Kullanıcılar, roller, konumlar ve kayıt altına alınan tüm önemli işlemler.",
  "admin.tab.users": "Kullanıcılar",
  "admin.tab.roles": "Roller",
  "admin.tab.locations": "Konumlar",
  "admin.tab.audit": "Denetim kaydı",
  "admin.newUser": "Yeni kullanıcı",
  "admin.inviteUser": "Kullanıcı davet et",
  "admin.editUser": "{email} düzenle",
  "admin.newLocation": "Yeni konum",
  "admin.editLocation": "{name} düzenle",
  "admin.newPassword": "Yeni parola (opsiyonel)",
  "admin.passwordHint": "Değiştirmemek için boş bırakın",
  "admin.userCreatedToast": "Kullanıcı oluşturuldu",
  "admin.userUpdatedToast": "Kullanıcı güncellendi",
  "admin.userDeleteConfirm": "{email} kullanıcısı silinsin mi?",
  "admin.locationCreatedToast": "Konum oluşturuldu",
  "admin.locationUpdatedToast": "Konum güncellendi",
  "admin.locationDeleteConfirm": "{name} konumu silinsin mi?",
  "admin.locationCreateCta": "Konum oluştur",
  "admin.userCreateCta": "Kullanıcı oluştur",
  "admin.cannotDeleteSelf": "Kendinizi silemezsiniz.",
  "admin.auditEmpty": "Henüz denetim kaydı yok.",

  "location.type.atelier": "atölye",
  "location.type.showroom": "showroom",
  "location.type.warehouse": "depo",
  "location.type.virtual": "sanal",
  "location.type.store": "mağaza",
  "location.type.consignment": "konsinye",

  "role.admin": "Yönetici",
  "role.management": "Üst Yönetim",
  "role.production_manager": "Üretim Müdürü",
  "role.atelier_staff": "Atölye Personeli",
  "role.inventory": "Envanter / Depo",
  "role.showroom_staff": "Showroom Personeli",
  "role.purchasing": "Satın Alma",
  "role.ecommerce": "E-ticaret",
  "role.finance": "Finans",

  "attach.savePrompt": "Fotoğraf ve belge eklemek için önce bu kaydı kaydedin.",
  "attach.drop": "Fotoğraf veya PDF sürükleyin ya da göz atmak için tıklayın",
  "attach.uploading": "Yükleniyor…",
  "attach.constraints": "JPG · PNG · WEBP · PDF · en fazla {mb} MB",
  "attach.empty": "Henüz ek yok.",
  "attach.deleteConfirm": "{name} silinsin mi?",
  "attach.uploadedToast": "{name} yüklendi",
  "attach.primary": "Ana",
  "attach.setPrimary": "Ana görsel yap",
  "attach.setPrimaryToast": "Ana görsel olarak ayarlandı",
  "attach.unsetPrimaryToast": "Ana görsel kaldırıldı",
  "attach.noKind": "— Tür —",
  "attach.kind.Front": "Ön",
  "attach.kind.Back": "Arka",
  "attach.kind.Side": "Yan",
  "attach.kind.Detail": "Detay",
  "attach.kind.Campaign": "Kampanya",
  "attach.kind.Lookbook": "Lookbook",
  "attach.kind.Sample": "Numune",
  "attach.kind.Technical": "Teknik çizim",
  "attach.kind.Invoice": "Fatura",
  "attach.kind.Contract": "Sözleşme",
  "attach.kind.Other": "Diğer",
  "view.list": "Liste",
  "view.grid": "Izgara",
  "styles.thumb": "Görsel",
  "styles.noImage": "Görsel yok",

  "coming.eyebrow": "2. · 3. Faz",
  "coming.title": "Sonraki fazda geliyor",
  "coming.subtitle": "Veri modeli hazır. Bu bölüm bir sonraki yapıda hayata geçecek.",
  "coming.orders.title": "Siparişler",
  "coming.orders.sub": "Shopify, showroom, toptan ve siparişe özel üretim için tek sipariş ekranı.",
  "coming.production.title": "Üretim",
  "coming.production.sub": "Kanban panosu, MRP, iş emirleri ve kalite kontrol — 2. Fazda geliyor.",
  "coming.purchasing.title": "Satın Alma",
  "coming.purchasing.sub": "Satın alma talepleri, siparişler, mal kabul ve tedarikçi puanlaması.",
  "coming.customers.title": "Müşteriler",
  "coming.customers.sub": "Hafif CRM ve VIP müşteri hizmeti.",
  "coming.reports.title": "Raporlar",
  "coming.reports.sub": "Stok, üretim ve satış için dışa aktarılabilir raporlar.",

  "common.image": "Görsel",
  "common.listView": "Liste görünümü",
  "common.gridView": "Izgara görünümü",

  "materials.form.colorName": "Renk adı",
  "materials.form.colorHex": "Renk HEX",
  "materials.form.colorPreview": "Renk önizleme",
  "rolls.photosHeading": "Top fotoğrafları",

  "nav.fabricLibrary": "Kumaş kütüphanesi",

  "library.eyebrow": "Malzemeler",
  "library.title": "Kumaş kütüphanesi",
  "library.subtitle": "NYLSU için mevcut tüm kumaşların görsel kataloğu — kategori, kompozisyon, tedarikçi veya stok durumuna göre filtreleyin.",
  "library.allCategories": "Tüm kategoriler",
  "library.allCompositions": "Tüm kompozisyonlar",
  "library.allSuppliers": "Tüm tedarikçiler",
  "library.anyAvailability": "Tüm stok durumları",
  "library.inStock": "Stokta",
  "library.outOfStock": "Tükendi",
  "library.composition": "Kompozisyon",
  "library.availability": "Stok durumu",
  "library.rolls": "top",
  "library.empty": "Seçilen filtrelere uygun kumaş bulunamadı.",

  // ─── Phase 2: Orders / Production / MTO / QC ───
  "common.loading": "Yükleniyor…",

  "nav.ordersAll": "Tüm siparişler",
  "nav.ordersShowroom": "Showroom siparişleri",
  "nav.ordersMTO": "Ismarlama üretim",
  "nav.reservations": "Rezervasyonlar",
  "nav.productionPlanning": "Üretim planlama",
  "nav.productionOrders": "Üretim emirleri",
  "nav.wipBoard": "WIP panosu",
  "nav.materialRequirements": "Malzeme gereksinimleri",
  "nav.qc": "Kalite kontrol",
  "nav.productionDashboard": "Üretim panosu",
  "nav.ordersDashboard": "Sipariş panosu",

  "customers.eyebrow": "Müşteriler",
  "customers.title": "Müşteriler",
  "customers.subtitle": "Perakende, toptan, showroom ve ısmarlama müşteriler.",
  "customers.new": "Yeni müşteri",
  "customers.edit": "Müşteriyi düzenle",
  "customers.empty": "Henüz müşteri yok.",
  "customers.createCta": "Müşteri oluştur",
  "customers.createdToast": "Müşteri oluşturuldu",
  "customers.updatedToast": "Müşteri güncellendi",
  "customers.deleteConfirm": "{name} silinsin mi?",
  "customers.company": "Şirket",
  "customers.vip": "VIP müşteri",
  "customer.type.retail": "Perakende",
  "customer.type.showroom": "Showroom",
  "customer.type.wholesale": "Toptan",
  "customer.type.mto": "Ismarlama",
  "customer.type.vip": "VIP",
  "customer.type.other": "Diğer",

  "orders.eyebrow": "Siparişler",
  "orders.title": "Tüm siparişler",
  "orders.showroomTitle": "Showroom siparişleri",
  "orders.subtitle": "Rezervasyondan teslimata kadar tüm siparişler.",
  "orders.new": "Yeni sipariş",
  "orders.empty": "Henüz sipariş yok.",
  "orders.createOrder": "Sipariş oluştur",
  "orders.createMto": "Ismarlama oluştur",
  "orders.createdToast": "Sipariş {number} oluşturuldu",
  "orders.confirmedToast": "Sipariş onaylandı. Stok kontrolü yapıldı.",
  "orders.reservedToast": "Stok rezerve edildi",
  "orders.reqCreatedToast": "Üretim gereksinimi oluşturuldu",
  "orders.cancelledToast": "Sipariş iptal edildi — rezervasyonlar serbest bırakıldı",
  "orders.cancelConfirm": "Bu siparişi iptal edip tüm rezervasyonlar bırakılsın mı?",
  "orders.orderNumber": "Sipariş #",
  "orders.customer": "Müşteri",
  "orders.source": "Kanal",
  "orders.priority": "Öncelik",
  "orders.date": "Sipariş tarihi",
  "orders.deliveryDate": "Teslim",
  "orders.eventDate": "Etkinlik tarihi",
  "orders.delivery": "Teslim",
  "orders.total": "Toplam",
  "orders.items": "Kalemler",
  "orders.qty": "Adet",
  "orders.reserved": "Rezerve",
  "orders.unitPrice": "Birim fiyat",
  "orders.confirm": "Siparişi onayla",
  "orders.cancel": "Siparişi iptal et",
  "orders.suggested": "Stok durumu & önerilen aksiyonlar",
  "orders.requested": "İstenen",
  "orders.availableStock": "Uygun stok",
  "orders.canReserve": "Rezerve edilebilir",
  "orders.needsProduction": "Üretim gerekli",
  "orders.reserveN": "{n} rezerve et",
  "orders.createReqN": "Üretim oluştur ({n})",
  "orders.selectStyle": "Model seç",
  "orders.selectVariant": "Renk / beden seç",
  "orders.addItem": "Kalem ekle",
  "orders.internalNotes": "Dahili notlar",
  "orders.col.style": "Model",
  "orders.col.variant": "Renk / Beden",
  "orders.col.qty": "Adet",
  "orders.col.unitPrice": "Birim fiyat",
  "orders.col.discount": "İskonto %",
  "orders.col.remove": "Sil",
  "orders.needAtLeastOneItem": "En az bir kalem gerekli",
  "orders.noDate": "Tarih yok",
  "orders.dashboardEyebrow": "Genel bakış",
  "orders.dashboardTitle": "Sipariş panosu",
  "orders.dashboardSub": "Bugün dikkat gerektiren tüm siparişler.",
  "orders.kpi.new": "Yeni siparişler",
  "orders.kpi.productionRequired": "Üretim gerekli",
  "orders.kpi.inProduction": "Üretimde",
  "orders.kpi.ready": "Hazır",
  "orders.kpi.late": "Gecikmiş",
  "orders.kpi.vipUrgent": "VIP / Acil",
  "orders.lateList": "Geciken siparişler",

  "order.source.manual": "Manuel",
  "order.source.showroom": "Showroom",
  "order.source.mto": "Ismarlama",
  "order.source.wholesale": "Toptan",
  "order.source.shopify": "Shopify",
  "order.source.paris": "Paris",
  "order.source.other": "Diğer",

  "order.status.draft": "Taslak",
  "order.status.confirmed": "Onaylı",
  "order.status.inventory_check": "Stok kontrolü",
  "order.status.reserved": "Rezerve",
  "order.status.production_required": "Üretim gerekli",
  "order.status.in_production": "Üretimde",
  "order.status.partially_ready": "Kısmen hazır",
  "order.status.ready": "Hazır",
  "order.status.packed": "Paketlendi",
  "order.status.delivered": "Teslim edildi",
  "order.status.cancelled": "İptal",

  "priority.normal": "Normal",
  "priority.high": "Yüksek",
  "priority.urgent": "Acil",
  "priority.vip": "VIP",

  "reservations.eyebrow": "Siparişler",
  "reservations.title": "Rezervasyonlar",
  "reservations.subtitle": "Onaylı siparişlere karşı rezerve edilen stok.",
  "reservations.empty": "Henüz rezervasyon yok.",
  "reservations.release": "Serbest bırak",
  "reservation.status.active": "Aktif",
  "reservation.status.released": "Serbest",
  "reservation.status.consumed": "Tüketildi",

  "mto.eyebrow": "Siparişler",
  "mto.title": "Ismarlama üretim",
  "mto.subtitle": "Kişisel ölçülere göre üretilen özel parçalar.",
  "mto.new": "Yeni ısmarlama sipariş",
  "mto.measurements": "Ölçüler",
  "mto.specialInstructions": "Özel talimatlar",
  "mto.m.bust": "Göğüs",
  "mto.m.waist": "Bel",
  "mto.m.hip": "Kalça",
  "mto.m.shoulder": "Omuz",
  "mto.m.back_width": "Sırt genişliği",
  "mto.m.dress_length": "Elbise boyu",
  "mto.m.sleeve_length": "Kol boyu",
  "mto.m.height": "Boy",

  "planning.eyebrow": "Üretim",
  "planning.title": "Üretim planlama",
  "planning.subtitle": "Talep, bitmiş stok ve üretim halindeki miktara göre net gereksinim.",
  "planning.empty": "Planlama gereksinimi yok.",
  "planning.style": "Model",
  "planning.demand": "Talep",
  "planning.availableFG": "Uygun bitmiş",
  "planning.inProduction": "Üretimde",
  "planning.net": "Net gereksinim",
  "planning.createPO": "Üretim emri oluştur",

  "po.eyebrow": "Üretim",
  "po.title": "Üretim emirleri",
  "po.subtitle": "BOM patlamasından bitmiş ürüne kadar tüm üretim emirleri.",
  "po.new": "Yeni üretim emri",
  "po.empty": "Henüz üretim emri yok.",
  "po.number": "Emir #",
  "po.qty": "Adet",
  "po.stage": "Aşama",
  "po.units": "adet",
  "po.sizeBreakdown": "Beden dağılımı",
  "po.needSizeBreakdown": "En az bir beden miktarı girin",
  "po.total": "Toplam",
  "po.plannedCompletion": "Planlanan bitiş",
  "po.completion": "Bitiş",
  "po.createCta": "Üretim emri oluştur",
  "po.createdToast": "Üretim emri {number} oluşturuldu",
  "po.materials": "Malzemeler",
  "po.materialAvailability": "Malzeme durumu",
  "po.material.green": "Tüm malzemeler mevcut",
  "po.material.amber": "Sipariş noktasına dikkat",
  "po.material.red": "Eksik — üretim karşılanamıyor",
  "po.stdQty": "Standart",
  "po.requiredQty": "Gerekli",
  "po.reservedQty": "Rezerve",
  "po.consumedQty": "Tüketilen",
  "po.availableQty": "Uygun",
  "po.shortage": "Eksik",
  "po.noMaterials": "BOM bağlı değil — model BOM ayarını kontrol edin.",
  "po.reserve": "Rezerve et",
  "po.consume": "Tüket",
  "po.reserveMaterial": "Malzeme rezerve et",
  "po.consumeMaterial": "Malzeme tüket",
  "po.reserveQty": "Rezerve miktarı",
  "po.actualQty": "Gerçek tüketim",
  "po.variance": "Sapma",
  "po.varianceReason": "Sapma nedeni",
  "po.consumeFromRoll": "Toptan",
  "po.rollAllocation": "Kumaş top ataması",
  "po.allocated": "Atanan",
  "po.advanceStage": "Aşamayı ilerlet",
  "po.advance": "İlerlet",
  "po.currentStage": "Mevcut aşama",
  "po.toStage": "Hedef aşama",
  "po.stageAdvancedToast": "Aşama ilerletildi",
  "po.receiveFinished": "Bitmiş ürünü kabul et",
  "po.receiveSku": "SKU",
  "po.availableToReceive": "mevcut",
  "po.receive": "Kabul et",
  "po.receivedToast": "Bitmiş ürün stoğa alındı",
  "po.reservedToast": "Malzeme rezerve edildi",
  "po.consumedToast": "Tüketim kaydedildi",
  "po.items": "Kalemler",
  "po.received": "Kabul edilen",
  "po.from": "Önce",
  "po.to": "Sonra",
  "po.stageHistory": "Aşama geçmişi",
  "po.cancel": "Emri iptal et",
  "po.cancelledToast": "Üretim emri iptal edildi",
  "po.cancelConfirm": "Bu üretim emri iptal edilsin mi? Rezerve malzemeler serbest bırakılacaktır.",

  "po.status.draft": "Taslak",
  "po.status.material_check": "Malzeme kontrolü",
  "po.status.material_shortage": "Malzeme eksik",
  "po.status.ready_to_start": "Başlamaya hazır",
  "po.status.in_production": "Üretimde",
  "po.status.qc": "Kalite kontrol",
  "po.status.completed": "Tamamlandı",
  "po.status.on_hold": "Beklemede",
  "po.status.cancelled": "İptal",

  "wip.eyebrow": "Üretim",
  "wip.title": "WIP panosu",
  "wip.subtitle": "Atölyedeki her parça — aşamayı ilerletmek için karta tıklayın.",
  "wip.openFullDetail": "Detayı aç →",

  "matreq.eyebrow": "Üretim",
  "matreq.title": "Malzeme gereksinimleri",
  "matreq.subtitle": "Açık üretim emirleri arasında konsolide malzeme eksiklikleri.",
  "matreq.empty": "Malzeme gereksinimi yok — her şey karşılanıyor.",
  "matreq.affectedPOs": "Etkilenen emirler",

  "qc.eyebrow": "Üretim",
  "qc.title": "Kalite kontrol",
  "qc.subtitle": "Her denetim kayıt altında — geçen parçalar satılabilir stoğa girer.",
  "qc.record": "QC kaydı",
  "qc.recordedToast": "QC kaydı oluşturuldu",
  "qc.empty": "Henüz QC kaydı yok.",
  "qc.sku": "SKU",
  "qc.inspected": "İncelenen",
  "qc.passed": "Geçen",
  "qc.failed": "Kalmış",
  "qc.failureReason": "Hata nedeni",
  "qc.disposition": "Karar",
  "qc.inspector": "Denetleyen",

  "prod.dashboardEyebrow": "Genel bakış",
  "prod.dashboardTitle": "Üretim panosu",
  "prod.dashboardSub": "Atölyenin nabzı.",
  "prod.kpi.openPOs": "Açık emirler",
  "prod.kpi.unitsInProduction": "Üretimde adet",
  "prod.kpi.completedToday": "Bugün tamamlanan",
  "prod.kpi.late": "Gecikmiş",
  "prod.kpi.atRisk": "Riskli",
  "prod.kpi.materialShortages": "Malzeme eksik",
  "prod.kpi.qcFailures": "QC kalmaları",
  "prod.byStage": "Aşamaya göre",

  "phase2.late": "Gecikmiş",
  "phase2.atRisk": "Riskli",

  // ── Phase 2.5 — Sales / POS
  "nav.salesDashboard": "Satış panosu",
  "nav.salesNew": "Yeni satış",
  "nav.salesAll": "Tüm satışlar",
  "nav.salesShowroom": "Showroom satışları",
  "nav.salesReturns": "İadeler",

  "common.date": "Tarih",

  "pos.eyebrow": "Satış",
  "pos.title": "Yeni satış",
  "pos.subtitle": "Fiziksel showroom satışı — stok, maliyet ve fatura sıralaması otomatik.",
  "pos.needLocation": "Önce bir satış lokasyonu seçin",
  "pos.searchPlaceholder": "Model, SKU, renk veya beden ara…",
  "pos.noResults": "Eşleşen ürün yok",
  "pos.available": "adet mevcut",
  "pos.outOfStock": "Tükendi",
  "pos.currentSale": "Mevcut satış",
  "pos.newSaleDraft": "Yeni satış (taslak)",
  "pos.lines": "kalem",
  "pos.cartEmpty": "Sepet boş — soldaki listeden ürün ekleyin.",
  "pos.searchScanPlaceholder": "Ürün, SKU ara veya barkod okut…",
  "pos.scan": "Okut",
  "pos.scanAdded": "{name} · {sku} eklendi",
  "pos.scanNotFound": "Barkod bulunamadı: {code}",
  "pos.scanInactive": "Barkod pasif",
  "pos.scanOutOfStock": "{name} — bu lokasyonda stok yok",
  "orders.priceFromMaster": "Fiyat Ürün Ana Verisinden alınır. Değiştirmek için yönetici izni gerekir.",
  "styles.searchPlaceholder": "Ürün adı, stil kodu, SKU veya barkod ara…",
  "suppliers.searchPlaceholder": "Tedarikçi adı, ilgili kişi, e-posta veya şehir ara…",
  "customers.searchPlaceholder": "Müşteri adı, telefon veya e-posta ara…",
  "inventory.searchPlaceholder": "Ürün, SKU, renk, beden veya malzeme kodu ara…",
  "bom.searchPlaceholder": "Ürün reçetesi ara (ad, kod veya SKU)…",
  "bom.subtitleList": "Her ürün tek satır. Reçetenin tamamı için tıklayın.",
  "bom.status": "Reçete durumu",
  "bom.status.no_bom": "Reçete yok",
  "bom.status.draft": "Taslak",
  "bom.status.ready": "Hazır",
  "bom.open": "Reçeteyi aç",
  "bom.backToList": "Ürün reçetelerine dön",
  "bom.editRecipe": "Reçeteyi düzenle",
  "bom.createRecipe": "+ Reçete oluştur",
  "bom.noRecipe": "Bu ürünün henüz reçetesi yok.",
  "common.all": "Tümü",
  "common.clearFilters": "Filtreleri temizle",
  "common.noMatches": "Mevcut filtrelere uyan sonuç yok.",
  "common.delete": "Sil",
  "pos.walkIn": "Rastgele müşteri",
  "stock.addStock": "Stok ekle",
  "stock.openingStockHeading": "Açılış stoku (opsiyonel)",
  "stock.openingStockHint": "Bu malzemenin elinizde fiziksel stoku varsa miktarı girin; ledger'a bir OPENING STOCK hareketi oluşturulur.",
  "stock.physical": "Fiziksel",
  "stock.reserved": "Rezerve",
  "stock.available": "Kullanılabilir",
  "stock.direction": "Yön",
  "stock.increase": "Artır (+)",
  "stock.decrease": "Azalt (−)",
  "stock.afterAdjustment": "Düzeltme sonrası:",
  "stock.belowReserved": "Düzeltme fiziksel stoku rezerve edilen miktarın altına düşüremez.",
  "stock.needLocation": "Lokasyon zorunludur.",
  "stock.needQty": "Miktar sıfırdan büyük olmalıdır.",
  "stock.saved": "Stok hareketi kaydedildi.",
  "common.reason": "Neden",
  "common.notes": "Notlar",
  "costing.panelTitle": "Maliyet & Fiyatlama",
  "costing.noBom": "Aktif reçete yok. Oluşturduğunuzda maliyet görünür.",
  "costing.materials": "Hammaddeler",
  "costing.labor": "İşçilik",
  "costing.overhead": "Genel gider",
  "costing.totalCost": "Toplam ürün maliyeti",
  "costing.materialsPct": "Hammadde payı",
  "costing.laborPct": "İşçilik payı",
  "costing.overheadPct": "Genel gider payı",
  "costing.method": "Fiyatlama yöntemi",
  "costing.markup": "Kârlılık (markup)",
  "costing.margin": "Hedef marj",
  "costing.suggestedPrice": "Önerilen satış fiyatı",
  "costing.currentRetail": "Mevcut perakende fiyat",
  "costing.gpAtRetail": "Mevcut fiyatta brüt kâr",
  "costing.gmAtRetail": "Mevcut fiyatta brüt marj",
  "costing.applyPrice": "Önerilen fiyatı uygula",
  "costing.applyConfirm": "Mevcut perakende fiyat önerilen fiyat ile değiştirilsin mi?",
  "costing.applied": "Perakende fiyat BOM maliyetinden güncellendi.",
  "costing.incompleteWarn": "Maliyet eksik — fiyatı olmayan malzeme: {missing}",
  "costing.sectionMaterials": "Hammaddeler",
  "costing.sectionLabor": "İşçilik",
  "costing.sectionOverhead": "Genel gider",
  "costing.laborType": "İşçilik türü",
  "costing.atelier": "Atölye / usta",
  "costing.operation": "Operasyon",
  "costing.cost": "Maliyet",
  "costing.unitCost": "Birim maliyet",
  "costing.lineCost": "Satır maliyeti",
  "costing.noLabor": "Henüz işçilik satırı yok.",
  "costing.fixed": "Ürün başına sabit",
  "costing.percent": "Malzeme + işçilik %'si",
  "costing.missing": "Maliyet eksik",
  "common.quantity": "Adet",
  "common.none": "Yok",
  "pos.customerSearch": "İsim veya telefon ile müşteri ara",
  "pos.customerSearchHint": "En az 2 karakter veya telefon için 3+ rakam yazın",
  "pos.customerNoResults": "Müşteri bulunamadı",
  "pos.customerCreate": "+ Yeni müşteri oluştur",
  "pos.customerSelected": "Müşteri",
  "pos.customerChange": "Değiştir",
  "pos.customerClear": "Rastgele müşteri",
  "pos.newCustomer": "Yeni müşteri",
  "pos.customerName": "Ad soyad",
  "pos.customerPhone": "Telefon",
  "pos.customerEmail": "E-posta",
  "pos.customerCity": "Şehir",
  "pos.customerType": "Tip",
  "pos.customerSave": "Kaydet ve seç",
  "pos.customerCancel": "İptal",
  "pos.customerDuplicate": "Bu telefon numarasına sahip bir müşteri zaten var.",
  "pos.customerDuplicateSelect": "Mevcut olanı seç",
  "pos.customerDuplicateContinue": "Yine de oluştur",
  "pos.customerCreated": "Müşteri oluşturuldu",
  "pos.summary": "Özet",
  "pos.subtotal": "Ara toplam",
  "pos.itemDiscount": "Kalem iskontosu",
  "pos.saleDiscount": "Satış iskontosu",
  "pos.netSales": "Net satış",
  "pos.tax": "KDV",
  "pos.total": "Toplam",
  "pos.amountPaid": "Ödendi",
  "pos.balanceDue": "Kalan bakiye",
  "pos.discount": "İskonto",
  "pos.reason": "Neden",
  "pos.apply": "Uygula",
  "pos.discountNote": "%10 üzeri iskontolar için yönetici onayı gerekir.",
  "pos.payment": "Ödeme",
  "pos.recordPayment": "Kaydet",
  "pos.recordSplit": "Ödemeyi kaydet",
  "pos.splitRecorded": "{n} ödeme kaydedildi",
  "pos.preset.cardAll": "Kart · tümü",
  "pos.preset.cashAll": "Nakit · tümü",
  "pos.preset.halfHalf": "50 / 50",
  "sales.viewReceipt": "Fiş",
  "common.back": "Geri",
  "pos.completeSale": "Satışı tamamla",
  "pos.completePartial": "Bakiyeli tamamla",
  "pos.discountApplied": "İskonto uygulandı",
  "pos.paymentAmountRequired": "Ödeme tutarı girin",
  "pos.paymentRecorded": "Ödeme kaydedildi",
  "pos.saleCompleted": "Satış {number} tamamlandı",

  "sales.eyebrow": "Satış",
  "sales.title": "Tüm satışlar",
  "sales.showroomTitle": "Showroom satışları",
  "sales.subtitle": "Tüm fiziksel, ısmarlama ve kanal satışları — maliyet ve fatura durumu ile.",
  "sales.new": "Yeni satış",
  "sales.empty": "Henüz satış yok.",
  "sales.number": "Satış #",
  "sales.channel": "Kanal",
  "sales.units": "Adet",
  "sales.grossProfit": "Brüt kâr",
  "sales.margin": "Marj",
  "sales.invoice": "Fatura",
  "sales.discount": "İsk.",
  "sales.unitCost": "Birim maliyet",
  "sales.integrations": "Dış entegrasyonlar",
  "sales.invoiceId": "Fatura No",
  "sales.retry": "Yeniden dene",
  "sales.retryQueued": "Yeniden deneme sıraya alındı",
  "sales.print": "Özet yazdır",
  "sales.void": "İptal",
  "sales.voidConfirm": "Bu satış iptal edilsin mi? Tamamlanmışsa stok geri yüklenecektir.",
  "sales.voided": "Satış iptal edildi",
  "sales.fullyPaid": "Tamamı ödendi",
  "sales.dashEyebrow": "Genel bakış",
  "sales.dashTitle": "Satış panosu",
  "sales.dashSub": "Bugün, bu ay ve showroom’da hareket gören modeller.",
  "sales.topStyles": "En çok satan modeller",
  "sales.kpi.todayNet": "Bugün · net satış",
  "sales.kpi.todayUnits": "Bugün · adet",
  "sales.kpi.mtdNet": "Ay · net satış",
  "sales.kpi.mtdMargin": "Ay · brüt marj",
  "sales.kpi.mtdProfit": "Ay · brüt kâr",
  "sales.kpi.avgSale": "Bugün ort. satış",
  "sales.kpi.returns": "İadeler",
  "sales.kpi.failedInvoices": "Başarısız fatura senkleri",

  "sale.channel.showroom": "Showroom",
  "sale.channel.manual": "Manuel",
  "sale.channel.shopify": "Shopify",
  "sale.channel.mto": "Ismarlama",
  "sale.channel.wholesale": "Toptan",
  "sale.channel.paris": "Paris",
  "sale.channel.other": "Diğer",
  "sale.status.draft": "Taslak",
  "sale.status.completed": "Tamamlandı",
  "sale.status.voided": "İptal",

  "payment.method.credit_card": "Kredi kartı",
  "payment.method.cash": "Nakit",
  "payment.method.bank_transfer": "Havale",
  "payment.method.payment_link": "Ödeme linki",
  "payment.method.other": "Diğer",
  "payment.status.unpaid": "Ödenmedi",
  "payment.status.partial": "Kısmi",
  "payment.status.paid": "Ödendi",
  "payment.status.refunded": "İade edildi",
  "payment.status.partially_refunded": "Kısmen iade",

  "invoice.status.pending": "Beklemede",
  "invoice.status.processing": "İşleniyor",
  "invoice.status.synced": "Senkron",
  "invoice.status.failed": "Başarısız",
  "invoice.status.retrying": "Tekrar deniyor",
  "invoice.status.not_required": "Gerekli değil",

  "returns.eyebrow": "Satış",
  "returns.title": "İadeler",
  "returns.subtitle": "Denetlenmiş, sınıflandırılmış ve stoğa yansıtılmış iadeler.",
  "returns.new": "Yeni iade",
  "returns.empty": "Henüz iade yok.",
  "returns.number": "İade #",
  "returns.reason": "Neden",
  "returns.refund": "İade tutarı",
  "returns.originalSale": "Orijinal satış",
  "returns.saleNotFound": "Satış bulunamadı",
  "returns.load": "Yükle",
  "returns.returnQty": "İade adedi",
  "returns.disposition": "Karar",
  "returns.pickAtLeastOne": "En az bir kalem seçin",
  "returns.createCta": "İade oluştur",
  "returns.createdToast": "İade oluşturuldu",
  "returns.completedToast": "İade denetimi tamamlandı",
  "returns.completeInspection": "Denetimi tamamla",
};

const dicts = { en, tr };

export function translate(lang, key, params) {
  const d = dicts[lang] || dicts.en;
  let s = d[key];
  if (s == null) s = dicts.en[key];
  if (s == null) return typeof params === "string" ? params : key;
  if (params && typeof params === "object") {
    for (const [k, v] of Object.entries(params)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}

export const SUPPORTED_LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "tr", label: "Türkçe", short: "TR" },
];

export default dicts;
